<template>
  <main id="content" aria-labelledby="main-title" v-if="page">

    <div class="hero--info bg-faun">
      <div class="pt-16 sm:pt-24">
        <Container>
          <FadeIn class="text-center max-w-3xl mx-auto">
            <h1 id="main-title" class="text-section-title">
              {{ page.title }}
            </h1>
            <p class="text-subtitle">
              {{ page.subtitle }}
            </p>
          </FadeIn>
        </Container>
      </div>
    </div>

    <ElementBlogCategories :categories="categories" />

    <Section margins="reset-top">
      <Container>

        <FadeIn
        v-if="articles.length > 3"
        delay="200"
        class="lg:grid lg:grid-cols-2 gap-8" data-term-id="all" data-term-name="All articles">
          <div class="mb-8 lg:mb-0">
            <ElementBlogCard :article="leadArticle" size="feature" />
          </div>
          
          <div class="grid gap-4">
            <ElementBlogCardList :article="article" v-for="(article, index) in listArticles" :key="'listArticle'+index" />
          </div>
        </FadeIn>

        <FadeIn 
        delay="200"
        class="mx-auto grid grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-3"
        :class="articles.length > 3 ? 'mt-8 md:mt-16' : 'mt-0 md:mt-0'"
        >
          <ElementBlogCard v-for="(article, index) in restArticles" :key="'article-'+index" :article="article" />
        </FadeIn>

      </Container>
    </Section>

  </main>
</template>

<script setup>
const route = useRoute()
const { getItems } = useCms()
const postStatus = useRuntimeConfig().public.postStatus

const currentLocale = computed(() => {
  return route.path.startsWith('/nz') ? 'nz' : 'au'
})

const { data } = useAsyncData(
  `${currentLocale.value}-category-page-${route.params.slug}`,
  async () => {

    const pageData = await getItems({
      collection: 'blog',
      params: {
        fields: [
          '*',
          'seo.*',
          'seo.meta_title',
          'seo.meta_description',
          'seo.meta_image.*'
        ]
      }
    })

    const articlesData = await getItems({
      collection: 'article',
      params: {
        filter: {
          locale: {
            _contains: currentLocale.value
          },
          status: {
            _in: postStatus
          }
        },
        fields: [
          '*', 'image.id', 'image.filename_download', 'image.description', 'image.width', 'image.height', 'categories.category_id.title', 'categories.category_id.slug',
        ],
        sort: "-date"
      }
    })
    
    const categoriesData = await getItems({
      collection: 'category',
      params: {
        filter: {
          locale: {
            _contains: currentLocale.value
          }
        },
        fields: [
          '*'
        ]
      }
    })
    
    // Return all data in a single object
    return {
      page: pageData,
      articles: articlesData,
      categories: categoriesData
    }
  }
)

const page = computed(() => data.value?.page || null)
const articles = computed(() => data.value?.articles || [])
const categories = computed(() => data.value?.categories || [])

const leadArticle = computed(() => {
  if (articles.value.length > 3) {
    return articles.value[0]
  }
  return null
})

const listArticles = computed(() => {
  if (articles.value.length > 3) {
    return [articles.value[1], articles.value[2], articles.value[3]]
  }
  return null
})

const restArticles = computed(() => {
  if (articles.value.length > 4) {
    return articles.value.slice(4)
  }
  return articles.value
})

// SEO handling
const seo = computed(() => {
  if (page.value?.seo) {
    return useSeo(page.value.seo, route.fullPath)
  }
  return {}
})

useHead(seo)

</script>